/**
 * The land profile — first run, and the one screen that turns grey into green.
 *
 * ── Why this screen exists before anything else ──────────────────────────────
 *
 * `shared/access.ts` emits a CAUTION for every land column a rule needed and
 * found NULL — never a pass. The seeded profile is all NULLs, so a new install
 * is grey NOT FULLY VERIFIED on every opportunity, and it stays that way until
 * somebody has walked the parcel with a tape measure. That is deliberate: an
 * unmeasured gate producing a green light is how somebody drives a trailer to
 * a gate it does not fit through.
 *
 * So the screen leads with **Measure these** — `rollUp().missing`, straight
 * from the server, in the order the rules care about — rather than with a form.
 * The list is the job; the form is how you do it.
 *
 * ── The assessment runs against an EMPTY envelope ────────────────────────────
 *
 * `GET /api/land/:id` compares the profile against no structure at all, on
 * purpose. With nothing to measure against, every rule that needs a land
 * column reports the column it could not read, and what comes back is exactly
 * the measuring list. Nothing here computes that; the same function the
 * warnings use produces it.
 *
 * ── Coordinates ─────────────────────────────────────────────────────────────
 *
 * The parcel's latitude and longitude are needed by the distance factor and by
 * the map, and they are the one value in this application that is never
 * logged, never put in an error body and never rendered on a public surface.
 *
 * They are asked for on the CREATE step only, because that is the only place
 * `land.ts` accepts them: `POST /api/land` writes the `hh_locations` row,
 * `PATCH` does not touch it. Offering the fields on the edit form as well
 * would be two inputs that silently do nothing, which is worse than not
 * offering them at all.
 */

import { useCallback, useEffect, useState } from 'react'
import { get, patch, post } from '../../lib/api'
import { useLocale, type Locale } from '../../lib/i18n'
import { measurementLabel } from '../../lib/domain'
import { TONES } from '../../lib/format'
import { Banner, Chip, S as PS } from '../../components/detail/ui'

/* ────────────────────────────────────────────────────────────────────────────
 * Server shapes
 * ────────────────────────────────────────────────────────────────────────── */

interface LandProfile {
  id: string
  name: string
  is_default: number
  road_access: string | null
  road_width_ft: number | null
  gate_width_ft: number | null
  gate_height_ft: number | null
  overhead_clearance_ft: number | null
  turning_radius_ft: number | null
  max_length_ft: number | null
  max_width_ft: number | null
  max_height_ft: number | null
  max_weight_lb: number | null
  slope_pct: number | null
  ground_condition: string | null
  crane_access: string | null
  power: string | null
  water: string | null
  septic: string | null
  drainage_notes: string | null
  flood_notes: string | null
  notes: string | null
}

interface LandView {
  profile: LandProfile
  access: { level: 'red' | 'amber' | 'grey' | 'green'; label: string; missing: string[] }
}

/* ────────────────────────────────────────────────────────────────────────────
 * The fields, and their vocabularies
 *
 * A table rather than twenty hand-written inputs — every one of these is
 * "optional, nullable, range-checked", and writing that out twenty times is
 * how one of them quietly ends up unchecked. The keys and ranges are the same
 * ones `land.ts` validates against, so a value this form accepts is a value
 * the server accepts.
 * ────────────────────────────────────────────────────────────────────────── */

type FieldKind = 'number' | 'enum' | 'text'

interface Field {
  /** The API key. `land.ts`'s `FIELDS` uses exactly these. */
  key: string
  column: keyof LandProfile
  kind: FieldKind
  values?: readonly string[]
  min?: number
  max?: number
  unit?: 'ft' | 'lb' | 'pct'
}

const ROAD_ACCESS = ['paved', 'gravel', 'dirt', 'none'] as const
const GROUND = ['firm', 'soft', 'rock', 'mixed', 'unknown'] as const
const CRANE = ['yes', 'limited', 'no'] as const
const UTILITY = ['available', 'nearby', 'none', 'unknown'] as const

const FIELDS: readonly Field[] = [
  { key: 'roadAccess', column: 'road_access', kind: 'enum', values: ROAD_ACCESS },
  { key: 'roadWidthFt', column: 'road_width_ft', kind: 'number', min: 0, max: 200, unit: 'ft' },
  { key: 'gateWidthFt', column: 'gate_width_ft', kind: 'number', min: 0, max: 200, unit: 'ft' },
  { key: 'gateHeightFt', column: 'gate_height_ft', kind: 'number', min: 0, max: 100, unit: 'ft' },
  { key: 'overheadClearanceFt', column: 'overhead_clearance_ft', kind: 'number', min: 0, max: 100, unit: 'ft' },
  { key: 'turningRadiusFt', column: 'turning_radius_ft', kind: 'number', min: 0, max: 500, unit: 'ft' },
  { key: 'maxLengthFt', column: 'max_length_ft', kind: 'number', min: 0, max: 500, unit: 'ft' },
  { key: 'maxWidthFt', column: 'max_width_ft', kind: 'number', min: 0, max: 200, unit: 'ft' },
  { key: 'maxHeightFt', column: 'max_height_ft', kind: 'number', min: 0, max: 100, unit: 'ft' },
  { key: 'maxWeightLb', column: 'max_weight_lb', kind: 'number', min: 0, max: 500000, unit: 'lb' },
  { key: 'slopePct', column: 'slope_pct', kind: 'number', min: 0, max: 100, unit: 'pct' },
  { key: 'groundCondition', column: 'ground_condition', kind: 'enum', values: GROUND },
  { key: 'craneAccess', column: 'crane_access', kind: 'enum', values: CRANE },
  { key: 'power', column: 'power', kind: 'enum', values: UTILITY },
  { key: 'water', column: 'water', kind: 'enum', values: UTILITY },
  { key: 'septic', column: 'septic', kind: 'enum', values: UTILITY },
  { key: 'drainageNotes', column: 'drainage_notes', kind: 'text' },
  { key: 'floodNotes', column: 'flood_notes', kind: 'text' },
  { key: 'notes', column: 'notes', kind: 'text' },
]

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  title: 'The land',
  intro:
    'Every access warning is arithmetic against these numbers. A measurement nobody has taken cannot produce a warning, so an unmeasured parcel is grey — NOT FULLY VERIFIED — and never green.',
  measureThese: 'Measure these',
  measureThisWhy: 'Each one turns a “we do not know” into a real answer for every opportunity.',
  allMeasured: 'Everything a rule needs is measured.',
  loading: 'Loading…',
  failed: 'The land profile could not be loaded.',
  none: 'There is no land profile yet.',
  create: 'Create the land profile',
  nameLabel: 'What to call it',
  namePlaceholder: 'The parcel',
  save: 'Save',
  saving: 'Saving…',
  saved: 'Saved.',
  saveFailed: 'That could not be saved.',
  unset: 'Not measured',
  utilities: 'What the land already has',
  notesHeading: 'Notes',
  coords: 'Where it is',
  coordsWhy: 'Used by the distance factor and the map. Never logged, and never shown on a public surface.',
  lat: 'Latitude',
  lng: 'Longitude',
  municipality: 'Municipality',
  makeDefault: 'Use this as the destination',
  isDefault: 'This is the destination land',

  road_access: 'What the road is made of',
  road_paved: 'Paved',
  road_gravel: 'Gravel',
  road_dirt: 'Dirt',
  road_none: 'No road',
  ground_firm: 'Firm',
  ground_soft: 'Soft',
  ground_rock: 'Rock',
  ground_mixed: 'Mixed',
  ground_unknown: 'Not sure',
  crane_yes: 'Yes',
  crane_limited: 'Limited',
  crane_no: 'No',
  util_available: 'Already there',
  util_nearby: 'Nearby',
  util_none: 'None',
  util_unknown: 'Not sure',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'El terreno',
  intro:
    'Cada aviso de acceso es aritmética con estos números. Una medida que nadie ha tomado no puede producir un aviso, así que un terreno sin medir queda gris — NO VERIFICADO DEL TODO — y nunca verde.',
  measureThese: 'Mide estas',
  measureThisWhy: 'Cada una convierte un “no sabemos” en una respuesta de verdad para todas las oportunidades.',
  allMeasured: 'Todo lo que una regla necesita ya está medido.',
  loading: 'Cargando…',
  failed: 'No se pudo cargar el perfil del terreno.',
  none: 'Todavía no hay un perfil del terreno.',
  create: 'Crear el perfil del terreno',
  nameLabel: 'Cómo llamarlo',
  namePlaceholder: 'El terreno',
  save: 'Guardar',
  saving: 'Guardando…',
  saved: 'Guardado.',
  saveFailed: 'Eso no se pudo guardar.',
  unset: 'Sin medir',
  utilities: 'Lo que el terreno ya tiene',
  notesHeading: 'Notas',
  coords: 'Dónde está',
  coordsWhy: 'Lo usan el factor de distancia y el mapa. Nunca se registra ni se muestra en público.',
  lat: 'Latitud',
  lng: 'Longitud',
  municipality: 'Municipio',
  makeDefault: 'Usar este como destino',
  isDefault: 'Este es el terreno de destino',

  road_access: 'De qué es el camino',
  road_paved: 'Asfaltado',
  road_gravel: 'Grava',
  road_dirt: 'Tierra',
  road_none: 'No hay camino',
  ground_firm: 'Firme',
  ground_soft: 'Blando',
  ground_rock: 'Piedra',
  ground_mixed: 'Mixto',
  ground_unknown: 'No estoy seguro',
  crane_yes: 'Sí',
  crane_limited: 'Limitado',
  crane_no: 'No',
  util_available: 'Ya está',
  util_nearby: 'Cerca',
  util_none: 'No hay',
  util_unknown: 'No estoy seguro',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/** The enum option labels, by the prefix their field uses. */
function optionLabel(field: Field, value: string, t: (key: CopyKey) => string): string {
  const prefix =
    field.key === 'roadAccess'
      ? 'road'
      : field.key === 'groundCondition'
        ? 'ground'
        : field.key === 'craneAccess'
          ? 'crane'
          : 'util'
  const key = `${prefix}_${value}` as CopyKey
  return key in COPY.en ? t(key) : value
}

const F = {
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, marginTop: 14 } as const,
  input: {
    width: '100%',
    minHeight: 52,
    fontSize: 17,
    padding: '12px 14px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--line)',
    background: 'var(--bg)',
    color: 'var(--ink)',
    boxSizing: 'border-box',
  } as const,
  area: {
    width: '100%',
    minHeight: 88,
    fontSize: 16,
    padding: '12px 14px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--line)',
    background: 'var(--bg)',
    color: 'var(--ink)',
    boxSizing: 'border-box',
    resize: 'vertical',
  } as const,
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 } as const,
  chip: (on: boolean) => ({
    minHeight: 44,
    padding: '0 14px',
    borderRadius: 999,
    fontSize: 15,
    border: `1px solid ${on ? 'var(--brand-strong)' : 'var(--line)'}`,
    background: on ? 'var(--brand-soft)' : 'var(--surface)',
    color: 'var(--ink)',
    cursor: 'pointer',
  }),
  primary: (busy: boolean) => ({
    width: '100%',
    minHeight: 56,
    marginTop: 22,
    borderRadius: 'var(--radius)',
    border: 'none',
    background: busy ? 'var(--muted)' : 'var(--brand)',
    color: 'var(--brand-ink)',
    fontSize: 18,
    fontWeight: 800,
    cursor: busy ? 'progress' : 'pointer',
  }),
}

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

type Draft = Record<string, string>

function draftOf(profile: LandProfile): Draft {
  const draft: Draft = { name: profile.name }
  for (const field of FIELDS) {
    const value = profile[field.column]
    draft[field.key] = value === null || value === undefined ? '' : String(value)
  }
  return draft
}

export default function LandProfileForm() {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const [view, setView] = useState<LandView | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'empty' | 'failed'>('loading')
  const [draft, setDraft] = useState<Draft>({})
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)
  const [newName, setNewName] = useState('')
  const [newMunicipality, setNewMunicipality] = useState('')
  const [newLat, setNewLat] = useState('')
  const [newLng, setNewLng] = useState('')

  const load = useCallback(async () => {
    try {
      const list = await get<{ profiles: LandProfile[] }>('/api/land')
      const chosen = list.profiles.find((row) => row.is_default === 1) ?? list.profiles[0]
      if (!chosen) {
        setState('empty')
        return
      }
      const detail = await get<LandView>(`/api/land/${encodeURIComponent(chosen.id)}`)
      setView(detail)
      setDraft(draftOf(detail.profile))
      setState('ready')
    } catch {
      setState('failed')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const create = useCallback(async () => {
    const name = newName.trim()
    if (!name) return
    setBusy(true)
    try {
      // `isDefault` on the create: with no default land there is no landed-cost
      // estimate at all, and a first profile that is not the destination is a
      // profile that does nothing.
      const lat = Number(newLat.trim())
      const lng = Number(newLng.trim())
      await post<{ profileId: string }>('/api/land', {
        name,
        isDefault: true,
        ...(newMunicipality.trim() ? { municipality: newMunicipality.trim() } : {}),
        // Coordinates are accepted on the CREATE only — `land.ts`'s PATCH does
        // not touch `hh_locations`, so offering them on the edit form would be
        // a field that silently does nothing.
        ...(Number.isFinite(lat) && Number.isFinite(lng) && newLat.trim() && newLng.trim()
          ? { lat, lng }
          : {}),
      })
      setMessage({ tone: 'ok', text: t('saved') })
      await load()
    } catch {
      setMessage({ tone: 'warn', text: t('saveFailed') })
    } finally {
      setBusy(false)
    }
  }, [newName, newMunicipality, newLat, newLng, load, t])

  const save = useCallback(async () => {
    if (!view) return
    setBusy(true)
    setMessage(null)

    // An empty string is a deliberate "unmeasure it", which the server writes
    // as NULL — and NULL is a caution, not a pass. Clearing a number puts the
    // warning back rather than leaving a stale one standing.
    const body: Record<string, unknown> = { name: draft.name?.trim() || view.profile.name }
    for (const field of FIELDS) {
      const raw = (draft[field.key] ?? '').trim()
      if (raw === '') {
        body[field.key] = null
        continue
      }
      if (field.kind === 'number') {
        const num = Number(raw)
        if (!Number.isFinite(num)) continue
        body[field.key] = num
        continue
      }
      body[field.key] = raw
    }

    try {
      const updated = await patch<LandView & { ok: boolean }>(`/api/land/${encodeURIComponent(view.profile.id)}`, body)
      setView({ profile: updated.profile, access: updated.access })
      setDraft(draftOf(updated.profile))
      setMessage({ tone: 'ok', text: t('saved') })
    } catch {
      setMessage({ tone: 'warn', text: t('saveFailed') })
    } finally {
      setBusy(false)
    }
  }, [draft, view, t])

  if (state === 'loading') return <p style={PS.note}>{t('loading')}</p>
  if (state === 'failed') return <p style={PS.note}>{t('failed')}</p>

  if (state === 'empty') {
    return (
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>{t('title')}</h2>
        <p style={PS.note}>{t('none')}</p>
        <label style={F.label} htmlFor="hh-land-name">
          {t('nameLabel')}
        </label>
        <input
          id="hh-land-name"
          style={F.input}
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          placeholder={t('namePlaceholder')}
        />
        <label style={F.label} htmlFor="hh-land-municipality">
          {t('municipality')}
        </label>
        <input
          id="hh-land-municipality"
          style={F.input}
          value={newMunicipality}
          onChange={(event) => setNewMunicipality(event.target.value)}
          placeholder="Vieques"
        />

        <span style={F.label}>{t('coords')}</span>
        <p style={{ ...PS.note, marginTop: 0 }}>{t('coordsWhy')}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            style={F.input}
            value={newLat}
            onChange={(event) => setNewLat(event.target.value)}
            inputMode="decimal"
            aria-label={t('lat')}
            placeholder={t('lat')}
          />
          <input
            style={F.input}
            value={newLng}
            onChange={(event) => setNewLng(event.target.value)}
            inputMode="decimal"
            aria-label={t('lng')}
            placeholder={t('lng')}
          />
        </div>

        <button type="button" style={F.primary(busy)} onClick={() => void create()} disabled={busy}>
          {busy ? t('saving') : t('create')}
        </button>
      </div>
    )
  }

  if (!view) return null
  const missing = view.access.missing

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>{t('title')}</h2>
      <p style={PS.note}>{t('intro')}</p>

      {/* The list is the job. It comes from the same function the warnings use,
          so it can never disagree with them. */}
      {missing.length > 0 ? (
        <Banner tone={TONES.quiet} icon="alert" outlined>
          <strong>{t('measureThese')}</strong>
          <span style={{ display: 'block', marginTop: 6, ...PS.chipRow }}>
            {missing.map((key) => (
              <Chip key={key} tone={TONES.quiet} outlined>
                {measurementLabel(key, locale)}
              </Chip>
            ))}
          </span>
          <span style={{ display: 'block', marginTop: 6, fontSize: 12, opacity: 0.85 }}>{t('measureThisWhy')}</span>
        </Banner>
      ) : (
        <Banner tone={TONES.strong} icon="check">
          {t('allMeasured')}
        </Banner>
      )}

      {message && (
        <Banner tone={message.tone === 'ok' ? TONES.strong : TONES.warn} icon={message.tone === 'ok' ? 'check' : 'alert'}>
          {message.text}
        </Banner>
      )}

      {view.profile.is_default === 1 && (
        <div style={PS.chipRow}>
          <Chip tone={TONES.brand} icon="pin">
            {t('isDefault')}
          </Chip>
        </div>
      )}

      <label style={F.label} htmlFor="hh-land-title">
        {t('nameLabel')}
      </label>
      <input
        id="hh-land-title"
        style={F.input}
        value={draft.name ?? ''}
        onChange={(event) => setDraft({ ...draft, name: event.target.value })}
      />

      {FIELDS.map((field) => (
        <FieldRow
          key={field.key}
          field={field}
          value={draft[field.key] ?? ''}
          missing={missing.includes(field.key)}
          locale={locale}
          t={t}
          onChange={(next) => setDraft({ ...draft, [field.key]: next })}
        />
      ))}

      <button type="button" style={F.primary(busy)} onClick={() => void save()} disabled={busy}>
        {busy ? t('saving') : t('save')}
      </button>
    </div>
  )
}

function FieldRow({
  field,
  value,
  missing,
  locale,
  t,
  onChange,
}: {
  field: Field
  value: string
  missing: boolean
  locale: Locale
  t: (key: CopyKey) => string
  onChange: (next: string) => void
}) {
  const id = `hh-land-${field.key}`
  const label = measurementLabel(field.key, locale)

  return (
    <div>
      <label style={F.label} htmlFor={field.kind === 'enum' ? undefined : id}>
        {label}
        {missing && (
          <span style={{ marginLeft: 8 }}>
            <Chip tone={TONES.warn} outlined>
              {t('unset')}
            </Chip>
          </span>
        )}
      </label>

      {field.kind === 'enum' ? (
        <div style={F.chipRow} role="group" aria-label={label}>
          <button type="button" style={F.chip(value === '')} onClick={() => onChange('')}>
            {t('unset')}
          </button>
          {(field.values ?? []).map((option) => (
            <button key={option} type="button" style={F.chip(value === option)} onClick={() => onChange(option)}>
              {optionLabel(field, option, t)}
            </button>
          ))}
        </div>
      ) : field.kind === 'text' ? (
        <textarea id={id} style={F.area} value={value} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            id={id}
            style={F.input}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            inputMode="decimal"
            placeholder=""
          />
          <span style={{ fontSize: 14, color: 'var(--muted)', minWidth: 34 }}>
            {field.unit === 'lb' ? 'lb' : field.unit === 'pct' ? '%' : field.unit === 'ft' ? (locale === 'es-PR' ? 'pies' : 'ft') : ''}
          </span>
        </div>
      )}
    </div>
  )
}
